import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Checkbox, Form, Input, Modal, Select, Typography, message } from 'antd'
import { DesktopOutlined } from '@ant-design/icons'
import { provisionAppBuildAgent } from '../../request'

const { Text, Paragraph } = Typography

// Extend the existing AMS toolbar with a protected, one-time credential handoff.
// Registration never starts the Agent or claims a build task.
export default function AppBuildAgentRegistration() {
    const [form] = Form.useForm()
    const [visible, setVisible] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [credentials, setCredentials] = useState(null)
    const [error, setError] = useState('')
    const [saved, setSaved] = useState(false)
    const [copying, setCopying] = useState(false)
    const [showToken, setShowToken] = useState(false)
    const pending = useRef(false)
    const mounted = useRef(true)
    const issuedKeyInput = useRef(null)

    useEffect(() => () => { mounted.current = false }, [])
    useEffect(() => {
        if (visible && !credentials) form.resetFields()
        if (credentials && issuedKeyInput.current) issuedKeyInput.current.focus({ preventScroll: true })
    }, [visible, credentials, form])

    const open = () => {
        setCredentials(null)
        setSaved(false)
        setShowToken(false)
        setError('')
        setVisible(true)
    }

    const close = () => {
        if (pending.current || (credentials && !saved)) return
        // Never persist the Token in browser storage, URLs or logs.
        setCredentials(null)
        setSaved(false)
        setShowToken(false)
        setVisible(false)
    }

    const submit = async () => {
        if (pending.current || credentials) return
        pending.current = true
        let values
        try {
            values = await form.validateFields()
        } catch (_) {
            pending.current = false
            return
        }
        if (!mounted.current) { pending.current = false; return }
        setSubmitting(true)
        setError('')
        try {
            const result = await provisionAppBuildAgent({
                agentKey: values.agentKey.trim(),
                agentName: values.agentName.trim(),
                platform: 'darwin',
                architecture: values.architecture,
            })
            if (!result || !result.agentKey || !result.agentToken) {
                throw new Error('注册响应缺少凭据，请联系管理员核查，勿重复注册')
            }
            if (mounted.current) setCredentials({ agentKey: result.agentKey, agentToken: result.agentToken })
        } catch (failure) {
            if (mounted.current) setError(failure && failure.message ? failure.message : '注册失败，请检查网络或联系管理员')
        } finally {
            pending.current = false
            if (mounted.current) setSubmitting(false)
        }
    }

    const copyToken = async () => {
        setCopying(true)
        try {
            if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('clipboard unavailable')
            await navigator.clipboard.writeText(credentials.agentToken)
            if (mounted.current) message.success('已复制 Token，请保存到构建机的 .env，使用后清除剪贴板')
        } catch (_) {
            if (mounted.current) message.warning('无法访问剪贴板，请点击“显示 Token”后手动复制')
        } finally {
            if (mounted.current) setCopying(false)
        }
    }

    return <>
        <Button icon={<DesktopOutlined />} onClick={open}>注册构建机</Button>
        <Modal
            title={credentials ? '构建机已注册 · 保存凭据' : '注册构建机'}
            open={visible}
            width={600}
            destroyOnClose
            maskClosable={false}
            closable={!submitting && !credentials}
            keyboard={!submitting && !credentials}
            onCancel={close}
            footer={credentials
                ? <Button type="primary" disabled={!saved || copying} onClick={close}>已保存，关闭</Button>
                : <><Button disabled={submitting} onClick={close}>取消</Button><Button type="primary" loading={submitting} onClick={submit}>注册并生成 Token</Button></>}
        >
            {credentials ? <>
                <Alert className="app-builder-agent-notice" type="warning" showIcon message="Token 只显示一次，请立即安全保存"
                    description="后端只保存 Token 哈希，关闭或刷新后无法再次查看。它是构建机接入凭据，与公司 GitHub Token 不同，请勿分享或提交到代码仓库。" />
                <Form layout="vertical">
                    <Form.Item label="构建机标识 · APP_BUILDER_AGENT_KEY" htmlFor="app-builder-issued-agent-key"><Input ref={issuedKeyInput} id="app-builder-issued-agent-key" readOnly value={credentials.agentKey} /></Form.Item>
                    <Form.Item label="构建机 Token · APP_BUILDER_AGENT_TOKEN" htmlFor="app-builder-issued-agent-token" extra="默认隐藏；点击“显示 Token”可临时显示。">
                        <Input id="app-builder-issued-agent-token" type={showToken ? 'text' : 'password'} readOnly value={credentials.agentToken} autoComplete="off" />
                    </Form.Item>
                </Form>
                <div className="app-builder-agent-actions">
                    <Button onClick={copyToken} loading={copying}>复制 Token</Button>
                    <Button onClick={() => setShowToken(!showToken)} aria-pressed={showToken}>{showToken ? '隐藏 Token' : '显示 Token'}</Button>
                </div>
                <Paragraph className="app-builder-agent-instructions">将以上两项配置到 Mac mini 的 app-builder-agent .env 文件中。注册不会启动 Agent；启动前还需检查工具链和排队任务。</Paragraph>
                <Checkbox checked={saved} onChange={event => setSaved(event.target.checked)}>我已安全保存标识和 Token，知道关闭后无法再次查看</Checkbox>
            </> : <>
                <Alert className="app-builder-agent-notice" type="info" showIcon message="首次接入构建机时配置一次"
                    description="为 Mac mini 生成独立接入凭据，不需要重新填写 GitHub Token。注册后需在构建机配置并启动 Agent，才会上报在线状态和可用构建能力。" />
                {error ? <Alert className="app-builder-agent-notice" type="error" showIcon message={error}
                    description="若请求超时，可能已创建记录，请先联系管理员核查；标识已存在时不要通过改名反复注册。" /> : null}
                <Form form={form} layout="vertical" requiredMark={false} initialValues={{ agentKey: 'mac-builder-01', agentName: 'Mac mini Builder', architecture: 'arm64' }}>
                    <Form.Item name="agentKey" label="构建机标识" extra="用于 Agent 配置，必须唯一；示例：mac-builder-01。" rules={[
                        { required: true, whitespace: true, message: '请输入构建机标识' },
                        { transform: value => (value || '').trim(), pattern: /^[A-Za-z0-9._-]{1,128}$/, message: '限 1–128 位字母、数字、点、下划线或横线' },
                    ]}><Input disabled={submitting} maxLength={128} autoComplete="off" /></Form.Item>
                    <Form.Item name="agentName" label="构建机名称" extra="方便识别，可按所在位置命名。" rules={[{ required: true, whitespace: true, message: '请输入构建机名称' }, { max: 128, message: '名称最多 128 个字符' }]}>
                        <Input disabled={submitting} maxLength={128} />
                    </Form.Item>
                    <Form.Item label="构建机系统" htmlFor="app-builder-agent-system" extra="这是机器自身的系统，不是项目的构建平台；Windows / Linux 仍在各项目中选择。"><Input id="app-builder-agent-system" value="macOS" disabled /></Form.Item>
                    <Form.Item name="architecture" label="构建机芯片" rules={[{ required: true, message: '请选择构建机芯片' }]}>
                        <Select disabled={submitting}><Select.Option value="arm64">Apple Silicon（M 系列）</Select.Option><Select.Option value="x64">Intel</Select.Option></Select>
                    </Form.Item>
                </Form>
                <Text>注册不代表 Windows / Linux 工具链已就绪，也不会执行已有构建任务。</Text>
            </>}
        </Modal>
    </>
}
